/**
 * Name / phone / email as one control — B5 building contact, B7 sales rep, B9
 * consultant. The `contact` answer kind in src/core/types.ts.
 *
 * Partial contacts are normal and are not corrected here: a surveyor who has a
 * name and a mobile number and no email has told the truth. `isAnswered()` in
 * validation.ts already treats any one populated field as answered.
 */

import type { ReactNode } from 'react';


import type { Contact } from '../../core/types';
import { TextField } from './TextField';
import { formatPhone } from '../state/phone';

export interface ContactFieldProps {
  /** Shown as a group heading above the three inputs. */
  label: string;
  value: Contact | undefined;
  onChange: (next: Contact) => void;
  disabled?: boolean;
}

export function ContactField({
  label,
  value,
  onChange,
  disabled = false,
}: ContactFieldProps): ReactNode {
  const contact = value ?? {};
  const patch = (change: Partial<Contact>): void => onChange({ ...contact, ...change });

  return (
    <fieldset className="radiolist">
      {/* `.field__label`, not `.eyebrow`: this is the question prompt for the
       * three boxes under it, and a prompt is set as a prompt (>= 18px, ink)
       * rather than as a 13px uppercase divider. */}
      {/* `aria-label`, so the group's accessible name is the prompt alone —
       * the ⓘ inside the legend must not become part of it (D-028). */}
      <legend className="field__label radiolist__legend" data-legibility="question" aria-label={label}>
        {label}
      </legend>
      <TextField
        label="Name"
        value={contact.name ?? ''}
        autoComplete="name"
        disabled={disabled}
        onChange={(name) => patch({ name })}
      />
      <TextField
        label="Phone"
        value={formatPhone(contact.phone ?? '') || (contact.phone ?? '')}
        inputMode="tel"
        autoComplete="tel"
        disabled={disabled}
        onChange={(phone) => patch({ phone: formatPhone(phone) || phone })}
      />
      <TextField
        label="Email"
        value={contact.email ?? ''}
        inputMode="email"
        autoComplete="email"
        disabled={disabled}
        onChange={(email) => patch({ email })}
      />
    </fieldset>
  );
}
